import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import moment from "moment";
import { apiService } from "../../../services/apiService";
import { errorNotify, successNotify } from "../../../utils/toast";
import {
  buildVehicleSearchFilters,
  mapVehicleApiRow,
} from "./AssetsVehiclesTab";

export const buildVehicleFilterPayload = (searchText, searchFilters) => {
  const payload = {};

  if (searchText && String(searchText).trim() !== "") {
    payload.search_text = String(searchText).trim();
  }

  if (searchFilters && typeof searchFilters === "object") {
    Object.keys(searchFilters).forEach((key) => {
      const filter = searchFilters[key];
      if (
        filter &&
        filter.filter_type &&
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== "" &&
        !(Array.isArray(filter.value) && filter.value.length === 0)
      ) {
        payload[key] = filter;
      }
    });
  }

  return payload;
};

export const buildFilterChips = (searchText, searchFilters = {}) => {
  const chips = [];
  if (searchText && String(searchText).trim() !== "") {
    chips.push({
      label: "Search",
      operator: "contains",
      value: `"${String(searchText).trim()}"`,
    });
  }

  const labelMap = {
    registrationNumberFilter: "Registration Number",
    vehicleTypeFilter: "Vehicle Type",
    statusFilter: "Status",
    ownershipTypeFilter: "Ownership",
    tankCapacityFilter: "Tank Capacity",
    currentFuelFilter: "Current Fuel",
    odometerFilter: "Current Odometer",
    driverFilter: "Driver",
  };

  if (searchFilters && typeof searchFilters === "object") {
    Object.keys(searchFilters).forEach((key) => {
      const filter = searchFilters[key];
      if (
        filter &&
        filter.filter_type &&
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== "" &&
        !(Array.isArray(filter.value) && filter.value.length === 0)
      ) {
        const label =
          labelMap[key] ||
          key
            .replace(/Filter$/, "")
            .replace(/([A-Z])/g, " $1")
            .trim();
        let valStr = "";
        let op = "equals";
        if (Array.isArray(filter.value)) {
          op = "is one of";
          valStr = filter.value.map((v) => `"${v}"`).join(", ");
        } else if (typeof filter.value === "object") {
          valStr = JSON.stringify(filter.value);
        } else {
          valStr = `"${filter.value}"`;
        }
        chips.push({
          label,
          operator: op,
          value: valStr,
        });
      }
    });
  }

  return chips;
};

const TABLE_COLUMNS = [
  { header: "#", key: "rowNum", width: 8, align: "center" },
  { header: "VEHICLE ID", key: "vehicleId", width: 22, align: "left" },
  { header: "REGISTRATION NO", key: "regNo", width: 25, align: "left" },
  { header: "TYPE", key: "vehicleType", width: 27, align: "left" },
  { header: "MAKE / MODEL", key: "makeModel", width: 28, align: "left" },
  { header: "OWNERSHIP", key: "ownership", width: 20, align: "left" },
  { header: "STATUS", key: "status", width: 20, align: "center" },
  { header: "CURRENT FUEL", key: "fuel", width: 22, align: "left" },
  { header: "TANK CAPACITY", key: "tank", width: 23, align: "left" },
  { header: "FUEL EFFICIENCY", key: "fuelEff", width: 27, align: "left" },
  { header: "ODOMETER", key: "odometer", width: 23, align: "left" },
  { header: "DRIVER", key: "driver", width: 32, align: "left" },
];

export const exportVehiclesToPdf = (
  rows = [],
  {
    searchText = "",
    searchFilters = {},
    fileName = "vehicles-listing.pdf",
    openInNewTab = true,
  } = {}
) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const generatedAt = moment().format("DD-MM-YYYY hh:mm A");
  const filters = buildFilterChips(searchText, searchFilters);
  const hasFilters = filters.length > 0;
  const totalRecords = rows.length;

  // 1. Draw Page 1 Brand Header Box
  // Accent Top Lime Bar (#a8c63e)
  doc.setFillColor(168, 198, 62);
  doc.rect(10, 10, 277, 2, "F");

  // Border Box (#d9e1ea)
  doc.setDrawColor(217, 225, 234);
  doc.rect(10, 10, 277, 24, "S");

  // Company Branding
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(31, 66, 117); // #1f4275
  doc.text("J HOLDINGS", 15, 19);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139); // #64748b
  doc.text("FLEET MANAGEMENT SYSTEM", 15, 25);

  // Vertical Separator
  doc.setDrawColor(217, 225, 234);
  doc.line(90, 14, 90, 31);

  // Report Identity Kicker & Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(113, 133, 42); // #71852a
  doc.text("ASSETS MANAGEMENT REPORT", 96, 18);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(21, 52, 95); // #15345f
  doc.text("Vehicles Listing Report", 96, 27);

  // Right Meta Stat Boxes
  // Box 1: Generated On
  doc.setFillColor(246, 248, 251); // #f6f8fb
  doc.roundedRect(192, 13, 44, 18, 1, 1, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  doc.setTextColor(100, 116, 139);
  doc.text("GENERATED ON", 195, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(21, 52, 95);
  doc.text(generatedAt, 195, 26);

  // Box 2: Total Vehicles
  doc.setFillColor(246, 248, 251);
  doc.roundedRect(240, 13, 43, 18, 1, 1, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  doc.setTextColor(100, 116, 139);
  doc.text("TOTAL VEHICLES", 243, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(21, 52, 95);
  doc.text(`${totalRecords} Records`, 243, 26);

  // 2. Draw Report Criteria Card
  doc.setFillColor(244, 248, 232); // #f4f8e8
  doc.rect(10, 37, 277, 12, "F");

  // Left accent bar
  doc.setFillColor(168, 198, 62);
  doc.rect(10, 37, 2.5, 12, "F");

  // Border
  doc.setDrawColor(220, 231, 180); // #dce7b4
  doc.rect(10, 37, 277, 12, "S");

  // Criteria Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(21, 52, 95);
  doc.text("REPORT CRITERIA", 15, 41.5);

  // Criteria State
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(97, 116, 38); // #617426
  doc.text(hasFilters ? "FILTERED RESULT" : "ALL RECORDS", 252, 41.5);

  // Filter Chips / Tags
  if (hasFilters) {
    let chipX = 15;
    const chipY = 43.5;
    const maxChipWidth = 270;

    filters.forEach((chip) => {
      if (chipX >= maxChipWidth) return;
      const chipText = `${chip.label} : ${chip.operator} ${chip.value}`;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.5);
      const textWidth = Math.min(doc.getTextWidth(chipText) + 6, maxChipWidth - chipX);
      if (textWidth <= 10) return;

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(214, 225, 159); // #d6e19f
      doc.roundedRect(chipX, chipY, textWidth, 4.5, 1, 1, "FD");

      doc.setTextColor(31, 66, 117);
      doc.text(chipText, chipX + 3, chipY + 3.2, { maxWidth: textWidth - 4 });
      chipX += textWidth + 3;
    });
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(
      "No filters applied - this report includes all available vehicle records.",
      15,
      45.5
    );
  }

  // 3. Helper to draw Table Header
  const drawTableHeader = (startY) => {
    // Header background (#1f4275)
    doc.setFillColor(31, 66, 117);
    doc.rect(10, startY, 277, 6.5, "F");

    doc.setDrawColor(203, 213, 225); // #cbd5e1
    doc.rect(10, startY, 277, 6.5, "S");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.setTextColor(255, 255, 255);

    let colX = 10;
    TABLE_COLUMNS.forEach((col) => {
      const textX =
        col.align === "center"
          ? colX + col.width / 2
          : colX + 2;
      doc.text(col.header, textX, startY + 4.2, {
        align: col.align,
        maxWidth: col.width - 2,
      });
      colX += col.width;
    });
  };

  // Helper to draw Continuing Page Header (Page 2+)
  const drawPageContinuationHeader = () => {
    doc.setFillColor(31, 66, 117);
    doc.rect(10, 8, 277, 6, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text("J HOLDINGS  •  Vehicles Listing Report (Continued)", 14, 12.2);

    drawTableHeader(16);
  };

  // 4. Render Table Rows
  drawTableHeader(52);
  let currentY = 58.5;
  const rowHeight = 6;
  const bottomThreshold = 195;

  if (rows.length === 0) {
    doc.setFillColor(255, 255, 255);
    doc.rect(10, currentY, 277, 12, "F");
    doc.setDrawColor(219, 227, 238);
    doc.rect(10, currentY, 277, 12, "S");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("No matching vehicle records found.", 148, currentY + 7.5, {
      align: "center",
    });
  } else {
    rows.forEach((row, index) => {
      // Check for page break
      if (currentY + rowHeight > bottomThreshold) {
        doc.addPage("a4", "landscape");
        drawPageContinuationHeader();
        currentY = 22.5;
      }

      // Alternating row background
      if (index % 2 === 1) {
        doc.setFillColor(248, 250, 252); // #f8fafc
        doc.rect(10, currentY, 277, rowHeight, "F");
      } else {
        doc.setFillColor(255, 255, 255);
        doc.rect(10, currentY, 277, rowHeight, "F");
      }

      // Row border
      doc.setDrawColor(219, 227, 238); // #dbe3ee
      doc.rect(10, currentY, 277, rowHeight, "S");

      // Cell Values
      const rowNum = String(index + 1);
      const vehicleId = String(row.vehicleId || row.systemId || "-");
      const regNo = String(row.registrationNumber || "-").toUpperCase();
      const vehicleType = String(row.vehicleType || "-");
      const makeModel =
        [row.make, row.model].filter(Boolean).join(" / ") || "-";
      const ownership = String(row.ownershipType || "-");
      const statusRaw = String(row.status || "Active").toLowerCase();
      const statusText =
        typeof row.status === "string"
          ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
          : "Active";

      const currentFuel =
        row.current_fuel !== undefined &&
        row.current_fuel !== null &&
        row.current_fuel !== ""
          ? `${row.current_fuel} Ltr`
          : "0 Ltr";

      const tankCapacity = row.tankCapacity
        ? `${row.tankCapacity} Ltr`
        : "-";

      const fuelEff =
        row.currentFuelEfficiency !== "-" &&
        row.currentFuelEfficiency !== undefined &&
        row.currentFuelEfficiency !== null &&
        row.currentFuelEfficiency !== ""
          ? `${row.currentFuelEfficiency} km/l`
          : "-";

      const odometer = String(row.currentOdometer || "-");
      const driver =
        typeof row.assignedDriver === "object"
          ? row.assignedDriver?.name || "-"
          : row.assignedDriver || "-";

      // Draw Cells
      let cellX = 10;

      // Col 1: #
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(100, 116, 139);
      doc.text(rowNum, cellX + 4, currentY + 4, { align: "center" });
      cellX += 8;

      // Col 2: Vehicle ID
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.2);
      doc.setTextColor(31, 66, 117); // #1f4275
      doc.text(vehicleId, cellX + 1.5, currentY + 4, { maxWidth: 20 });
      cellX += 22;

      // Col 3: Registration Number
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.2);
      doc.setTextColor(15, 23, 42); // #0f172a
      doc.text(regNo, cellX + 1.5, currentY + 4, { maxWidth: 23 });
      cellX += 25;

      // Col 4: Type
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(vehicleType, cellX + 1.5, currentY + 4, { maxWidth: 25 });
      cellX += 27;

      // Col 5: Make / Model
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(makeModel, cellX + 1.5, currentY + 4, { maxWidth: 26 });
      cellX += 28;

      // Col 6: Ownership
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(ownership, cellX + 1.5, currentY + 4, { maxWidth: 18 });
      cellX += 20;

      // Col 7: Status Pill Badge
      const isAct = statusRaw.includes("active");
      const isMaint = statusRaw.includes("maint");
      const pillW = 16;
      const pillH = 4;
      const pillX = cellX + (20 - pillW) / 2;
      const pillY = currentY + (rowHeight - pillH) / 2;

      if (isAct) {
        doc.setFillColor(240, 253, 244); // #f0fdf4
        doc.setDrawColor(187, 247, 208); // #bbf7d0
        doc.roundedRect(pillX, pillY, pillW, pillH, 1, 1, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5);
        doc.setTextColor(21, 128, 61); // #15803d
        doc.text(statusText, pillX + pillW / 2, pillY + 2.8, {
          align: "center",
        });
      } else if (isMaint) {
        doc.setFillColor(255, 251, 235); // #fffbeb
        doc.setDrawColor(253, 230, 138); // #fde68a
        doc.roundedRect(pillX, pillY, pillW, pillH, 1, 1, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5);
        doc.setTextColor(180, 83, 9); // #b45309
        doc.text("Maintenance", pillX + pillW / 2, pillY + 2.8, {
          align: "center",
        });
      } else {
        doc.setFillColor(254, 242, 242); // #fef2f2
        doc.setDrawColor(254, 202, 202); // #fecaca
        doc.roundedRect(pillX, pillY, pillW, pillH, 1, 1, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5);
        doc.setTextColor(185, 28, 28); // #b91c1c
        doc.text(statusText, pillX + pillW / 2, pillY + 2.8, {
          align: "center",
        });
      }
      cellX += 20;

      // Col 8: Current Fuel
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(currentFuel, cellX + 1.5, currentY + 4, { maxWidth: 20 });
      cellX += 22;

      // Col 9: Tank Capacity
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(tankCapacity, cellX + 1.5, currentY + 4, { maxWidth: 21 });
      cellX += 23;

      // Col 10: Current Fuel Efficiency (km/l)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.2);
      doc.setTextColor(21, 52, 95); // #15345f
      doc.text(fuelEff, cellX + 1.5, currentY + 4, { maxWidth: 25 });
      cellX += 27;

      // Col 11: Odometer
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(odometer, cellX + 1.5, currentY + 4, { maxWidth: 21 });
      cellX += 23;

      // Col 12: Driver
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.8);
      doc.setTextColor(51, 65, 85);
      doc.text(driver, cellX + 1.5, currentY + 4, { maxWidth: 30 });

      currentY += rowHeight;
    });
  }

  // 5. Draw Footer on all pages
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(217, 225, 234);
    doc.line(10, 200, 287, 200);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(100, 116, 139);
    doc.text(
      "J HOLDINGS | Fleet Management System | Confidential Operational Report",
      10,
      204
    );
    doc.text(`Page ${p} of ${totalPages}`, 275, 204, { align: "right" });
  }

  // 6. Output PDF (open in new tab like other modules)
  if (openInNewTab) {
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const openedWindow = window.open(blobUrl, "_blank");
    if (!openedWindow) {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    doc.save(fileName);
  }
};

export const exportVehiclesToExcel = (
  rows = [],
  { fileName = "vehicles-listing.xlsx" } = {}
) => {
  const exportData = rows.map((row, index) => {
    const fuelEffVal =
      row.currentFuelEfficiency !== "-" &&
      row.currentFuelEfficiency !== undefined &&
      row.currentFuelEfficiency !== null &&
      row.currentFuelEfficiency !== ""
        ? `${row.currentFuelEfficiency} km/l`
        : "-";

    return {
      "#": index + 1,
      "Vehicle ID": row.vehicleId || row.systemId || "-",
      "Registration Number": row.registrationNumber || "-",
      "Vehicle Type": row.vehicleType || "-",
      Make: row.make || "-",
      Model: row.model || "-",
      Ownership: row.ownershipType || "-",
      Year: row.year || "-",
      Status:
        typeof row.status === "string"
          ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
          : "Active",
      "Current Fuel (Ltr)":
        row.current_fuel !== undefined &&
        row.current_fuel !== null &&
        row.current_fuel !== ""
          ? String(row.current_fuel)
          : "0",
      "Tank Capacity (Ltr)": row.tankCapacity || "-",
      "Current Fuel Efficiency (km/l)": fuelEffVal,
      "Current Odometer": row.currentOdometer || "-",
      Driver:
        typeof row.assignedDriver === "object"
          ? row.assignedDriver?.name || "-"
          : row.assignedDriver || "-",
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles Listing");
  XLSX.writeFile(workbook, fileName);
};

const EXPORT_CONFIG = {
  pdf: {
    request: (payload) => apiService.getVehicleListingPdf(payload),
    fileName: "vehicles-listing.pdf",
    fileType: "PDF",
    fallbackExport: (rows, options) => exportVehiclesToPdf(rows, options),
  },
  excel: {
    request: (payload) => apiService.getVehicleListingExcel(payload),
    fileName: "vehicles-listing.xlsx",
    fileType: "Excel",
    fallbackExport: (rows, options) => exportVehiclesToExcel(rows, options),
  },
};

export const useVehicleListingExport = ({
  searchText = "",
  searchFilters = {},
  vehicleApiSearchFilters = {},
  totalItems = 0,
  makeOptions = [],
  modelOptions = [],
  rows = [],
} = {}) => {
  const [loadingType, setLoadingType] = useState(null);

  // Buttons remain disabled until at least one filter or search text is present
  const hasActiveFilters = useMemo(() => {
    const hasSearchText = Boolean(
      searchText && String(searchText).trim().length > 0
    );
    const hasSearchFilters = Boolean(
      searchFilters &&
        typeof searchFilters === "object" &&
        Object.keys(searchFilters).some((key) => {
          const filter = searchFilters[key];
          if (!filter) return false;
          if (Array.isArray(filter.value)) return filter.value.length > 0;
          return (
            filter.value !== undefined &&
            filter.value !== null &&
            filter.value !== ""
          );
        })
    );
    return hasSearchText || hasSearchFilters;
  }, [searchText, searchFilters]);

  const handleExport = async (type) => {
    const config = EXPORT_CONFIG[type];
    if (!config || !hasActiveFilters || loadingType) return;

    setLoadingType(type);
    try {
      let handled = false;

      // 1. First attempt backend API export if backend endpoint exists
      try {
        const payload = buildVehicleFilterPayload(searchText, searchFilters);
        const response = await config.request(payload);
        const responseData = response?.data ?? response;
        const filePath =
          responseData?.url ||
          responseData?.file ||
          (typeof responseData === "string" ? responseData : null);

        if (filePath) {
          const backendBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/api\/?$/, "");
          const fileUrl = /^https?:\/\//i.test(filePath)
            ? filePath
            : filePath.startsWith("/public") || filePath.startsWith("/uploads")
            ? `${backendBaseUrl}${filePath}`
            : `${process.env.REACT_APP_API_FILES_URL}${filePath.replace(/^\//, "")}`;

          if (type === "pdf") {
            const openedWindow = window.open(fileUrl, "_blank");
            if (!openedWindow) {
              const link = document.createElement("a");
              link.href = fileUrl;
              link.target = "_blank";
              link.rel = "noopener noreferrer";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          } else {
            const link = document.createElement("a");
            link.href = fileUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.setAttribute("download", config.fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          handled = true;
          successNotify(
            `Vehicles listing ${config.fileType} ${
              type === "pdf" ? "opened in new tab" : "downloaded"
            } successfully`
          );
        }
      } catch (apiErr) {
        // Backend endpoint not ready, proceed to fetch complete dataset and export client-side
      }

      // 2. Fetch COMPLETE dataset across all pages for the active filter/search
      if (!handled) {
        let exportRows = [];
        try {
          const fetchLimit = Math.max(1000, Number(totalItems) || 1000);
          const response = await apiService.getVehiclesPaginated({
            page: 1,
            limit: fetchLimit,
            search_text: searchText,
            searchFilters:
              vehicleApiSearchFilters ||
              buildVehicleSearchFilters(searchFilters),
          });

          const rawList =
            response?.data?.results ||
            response?.data?.data ||
            response?.results ||
            response?.data ||
            [];

          if (Array.isArray(rawList) && rawList.length > 0) {
            exportRows = rawList.map((row) =>
              mapVehicleApiRow(row, {
                makeOptions,
                modelOptions,
              })
            );
          }
        } catch (fetchErr) {
          console.warn(
            "Could not fetch full vehicle records, using available rows:",
            fetchErr
          );
        }

        if (!exportRows.length) {
          exportRows = Array.isArray(rows) ? rows : [];
        }

        config.fallbackExport(exportRows, {
          searchText,
          searchFilters,
          fileName: config.fileName,
          openInNewTab: type === "pdf",
        });
        successNotify(
          `Vehicles listing ${config.fileType} ${
            type === "pdf" ? "opened in new tab" : "downloaded"
          } successfully`
        );
      }
    } catch (error) {
      const message = typeof error === "string" ? error : error?.message;
      errorNotify(
        message || `Failed to download vehicle listing ${config.fileType}`
      );
    } finally {
      setLoadingType(null);
    }
  };

  return {
    hasActiveFilters,
    exportLoading: Boolean(loadingType),
    pdfExportLoading: loadingType === "pdf",
    excelExportLoading: loadingType === "excel",
    handleExportPdf: () => handleExport("pdf"),
    handleExportExcel: () => handleExport("excel"),
  };
};

export default useVehicleListingExport;
